import { useState } from "react";
import { Link, type LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import {
  getPlatformUserMemberships,
  getPlatformUserMembershipsCancellationReasons,
  getUser,
} from "api/methods";
import { Models } from "api/types";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import Dropdown from "atoms/dropdown/Dropdown";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

import { getRemainingPaymentAttempts } from "./membershipHelper";

const load = async ({
  platformUserMembershipId,
  platformUserMembership,
}: {
  platformUserMembershipId: string;
  platformUserMembership: Models<"PlatformUserMembershipModel">;
}) => {
  const userInfo = queryClient.fetchQuery({ ...getUser.keyed() });
  const membershipCancellationReasons = queryClient.fetchQuery({
    ...getPlatformUserMembershipsCancellationReasons.keyed(),
  });

  return waitObj({
    platformUserMembershipId,
    userInfo,
    platformUserMembership,
    membershipCancellationReasons,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipDunningCancelLoader: LoaderFunction = async ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  const userMemberships = await queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  const platformUserMembership = userMemberships.find(
    (m) => m.platformUserMembershipId == platformUserMembershipId,
  );

  if (!platformUserMembership || platformUserMembership.dunningStage === 0) {
    return redirect("..");
  }

  return load({ platformUserMembershipId, platformUserMembership });
};

export const WalletMembershipDunningCancelPage = () => {
  const { t } = useTranslation();
  const {
    platformUserMembershipId,
    userInfo,
    platformUserMembership,
    membershipCancellationReasons,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();

  const cancellationReasons = membershipCancellationReasons.map((reasonInfo) => ({
    ...reasonInfo,
    name: reasonInfo.reason,
  }));
  const [selectedCancellationReasonId, setSelectedCancellationReasonId] = useState<string>();
  const selectedCancellationReason = cancellationReasons.find(
    ({ id }) => id === selectedCancellationReasonId,
  );

  const [cancellationReasonError, setCancellationReasonError] = useState<boolean>(false);

  const numberOfAttemptsLeft =
    platformUserMembership && getRemainingPaymentAttempts(platformUserMembership.dunningStage);

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipDunningCancelPage.header")}
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-7 bg-white px-8 pt-3 text-left">
        <div className="text-title">
          {t("walletMembershipDunningCancelPage.confirmCancelTitle", {
            membershipName: platformUserMembership?.membership.name,
          })}
        </div>
        <div className="flex flex-col gap-y-5 text-body1">
          <div>
            {t("walletMembershipDunningCancelPage.pausedIntro")}{" "}
            <span className="text-bold1">
              {t("walletMembershipDunningCancelPage.attemptsRemaining", {
                count: numberOfAttemptsLeft ?? 0,
              })}
            </span>{" "}
            {t("walletMembershipDunningCancelPage.pausedOutro")}
          </div>
          <div>{t("walletMembershipDunningCancelPage.cancellationEffectNotice")}</div>
          <div>
            {t("walletMembershipDunningCancelPage.confirmationEmailPrefix")}{" "}
            <span className="text-bold1">{userInfo.email}</span>
          </div>
        </div>
        <div className="flex shrink">
          <Link to="/account/details" className="flex text-bold1 text-brand-color">
            {t("walletMembershipDunningCancelPage.changeEmailLink")}
          </Link>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 py-5">
        <Dropdown
          options={cancellationReasons}
          value={selectedCancellationReasonId}
          setValue={(value) => {
            setSelectedCancellationReasonId(value);
            setCancellationReasonError(false);
          }}
          textValue={selectedCancellationReason?.name}
          label={t("walletMembershipDunningCancelPage.cancellationReasonLabel")}
          invalid={cancellationReasonError}
        />
        <Button
          fullWidth
          style="white"
          disabled={!selectedCancellationReasonId}
          onClick={() => {
            if (!selectedCancellationReasonId) {
              setCancellationReasonError(true);
              return;
            }

            navigate(`/wallet/memberships/manage/${platformUserMembershipId}/dunning/cancel`, {
              state: {
                membershipCancellationReasonId: selectedCancellationReasonId,
              },
            });
          }}
        >
          {t("walletMembershipDunningCancelPage.confirmCancelButton")}
        </Button>
        <Button
          fullWidth
          style="primary"
          onClick={() => {
            navigate(`/wallet/memberships/manage/${platformUserMembershipId}/payment-method`);
          }}
        >
          {t("walletMembershipDunningCancelPage.updatePaymentMethodButton")}
        </Button>
      </div>
    </div>
  );
};
